import { useEffect, useState } from "react"
import { toast } from "react-toastify"
import {
  useDropFilesMutation,
  useGetDroppedFilesQuery,
} from "~/ui-rtk/api/fileDropApi"
import { Button } from "~/ui-rtk/components/ui/controls"
import FileUpload from "~/ui-rtk/components/ui/controls/FileUpload"

const formatSize = (bytes: number) =>
  bytes >= 1024 * 1024
    ? `${(bytes / (1024 * 1024)).toFixed(1)} MB`
    : `${(bytes / 1024).toFixed(1)} KB`

export default function FileDropPage() {
  const [selectedFiles, setSelectedFiles] = useState<File[]>([])
  const [dropFiles, { isLoading }] = useDropFilesMutation()
  const { data: droppedFiles, isLoading: isListLoading } =
    useGetDroppedFilesQuery()

  useEffect(() => {
    document.title = "Marathon Data: Setup > File Drop"
  }, [])

  const handleFileSelect = (files: FileList | null) => {
    setSelectedFiles(files ? Array.from(files) : [])
  }

  const handleUpload = async () => {
    if (!selectedFiles.length) {
      toast.error("Please select at least one file to upload")
      return
    }

    try {
      const response = await dropFiles(selectedFiles).unwrap()
      const failed = response.result.filter(file => file.status === "error")

      if (failed.length) {
        failed.forEach(file =>
          toast.error(`${file.file}: ${file.message ?? "Upload failed"}`),
        )
      } else {
        toast.success(
          `${response.result.length} ${response.result.length > 1 ? "files" : "file"} uploaded successfully`,
        )
      }
      setSelectedFiles([])
    } catch {
      toast.error("Failed to upload files")
    }
  }

  return (
    <div className="max-w-3xl space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-white">File Drop</h2>
        <p className="text-sm text-platinum mt-1">
          Securely send files to the Marathon Data team. Files are stored
          privately for your company only — no format requirements.
        </p>
      </div>

      <FileUpload
        onUpload={handleFileSelect}
        accept="*"
        maxSize={150}
        maxFiles={10}
        isMultiple
        text="Select Files"
      />

      {selectedFiles.length > 0 && (
        <div className="p-3 bg-basic-dark-blue/50 border border-basic-blue rounded-lg space-y-1">
          {selectedFiles.map(file => (
            <p key={file.name} className="text-sm text-white">
              {file.name}{" "}
              <span className="text-platinum">({formatSize(file.size)})</span>
            </p>
          ))}
        </div>
      )}

      <Button
        variant={{ variant: "solid", color: "white" }}
        onClick={handleUpload}
        disabled={!selectedFiles.length || isLoading}
        isLoading={isLoading}
      >
        {isLoading ? "Uploading..." : "Upload"}
      </Button>

      <div>
        <h3 className="text-lg font-semibold text-white mb-2">
          Previously Sent
        </h3>
        {isListLoading && <p className="text-sm text-platinum">Loading...</p>}
        {!isListLoading && !droppedFiles?.length && (
          <p className="text-sm text-platinum">No files sent yet.</p>
        )}
        {!isListLoading && !!droppedFiles?.length && (
          <table className="table table-sm">
            <thead>
              <tr>
                <th>File</th>
                <th>Size</th>
                <th>Uploaded</th>
              </tr>
            </thead>
            <tbody>
              {droppedFiles.map(file => (
                <tr key={file.fileName}>
                  <td className="text-white">{file.fileName}</td>
                  <td>{formatSize(file.size)}</td>
                  <td>{new Date(file.uploadedAt).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}
